<template>
  <div
    ref="root"
    class="charts-frame-wrapper"
    :style="{ height: frameHeight, width: frameWidth }">
    <div
      v-if="!iframeReady"
      class="charts-skeleton"
      :class="skeletonClass"
      data-qa="charts-skeleton"/>
    <iframe
      v-if="loaded"
      v-show="iframeReady"
      ref="chartFrame"
      class="charts-frame"
      data-qa="charts-frame"
      frameborder="0"
      :src="iframeSrc"
      :style="{ height: frameHeight, width: frameWidth }"
      title="Chart"/>
  </div>
</template>

<script>
/**
 * Компонент для роботи з графіками (мікрофронтенд chart-view), за зразком SpilkaFormioMF.vue.
 */

import theme from 'mixins/theme'

const MSG = {
  // host → iframe
  RENDER: 'chart:render',
  THEME: 'chart:theme',
  // iframe → host
  READY: 'chart:ready',
  ERROR: 'chart:error',
  // Тултіп малює НЕ iframe (не може вийти за власні межі), а host — у document.body поверх
  // сторінки, за координатами курсора (у viewport iframe) + offset iframe.getBoundingClientRect().
  TOOLTIP_SHOW: 'chart:tooltip-show',
  TOOLTIP_HIDE: 'chart:tooltip-hide',
}

const DARK_THEME = 'dark-theme'

export default {
  name: 'spilkaChartsMF',
  mixins: [theme],
  inheritAttrs: false,
  data() {
    return {
      iframeReady: false,
      loaded: false,
      observer: null,
      tooltipEl: null,
    }
  },
  computed: {
    payload() {
      return { kind: this.kind, height: this.height, width: this.width, ...this.$attrs }
    },
    frameHeight() {
      return this.height ? `${this.height}px` : '300px'
    },
    iframeSrc() {
      const microserviceUrl = (window.config.MicroserviceUrl || '/').replace(/\/$/, '')
      return `${microserviceUrl}/app/mf/chart-view/`
      //return 'http://localhost:3005/app/mf/chart-view/'
    },
    iframeOrigin() {
      try { return new URL(this.iframeSrc, window.location.href).origin } catch { return '*' }
    },
    frameWidth() {
      return this.width ? `${this.width}px` : '100%'
    },
    skeletonClass() {
      return this.kind === 'donut' ? 'charts-skeleton--donut' : 'charts-skeleton--rect'
    },
  },
  methods: {
    postToFrame(type, payload = {}) {
      const frameWindow = this.$refs.chartFrame?.contentWindow
      if (!frameWindow) { return }
      frameWindow.postMessage({ type, ...payload }, this.iframeOrigin)
    },
    sendTheme() {
      this.postToFrame(MSG.THEME, { theme: this.userTheme === DARK_THEME ? 'dark' : 'light' })
    },
    pushToFrame() {
      if (!this.iframeReady) { return }
      this.postToFrame(MSG.RENDER, this.toPlain(this.payload))
    },
    /** Зняти реактивні обгортки перед postMessage (структурне клонування) */
    toPlain(value) {
      try {
        return JSON.parse(JSON.stringify(value))
      } catch (_) {
        return value
      }
    },
    onFrameMessage(event) {
      if (!this.$refs.chartFrame || event.source !== this.$refs.chartFrame.contentWindow) {
        return
      }
      const data = event.data
      if (!data || !data.type) { return }

      switch (data.type) {
      case MSG.READY:
        this.iframeReady = true
        this.pushToFrame()
        this.sendTheme()
        break
      case MSG.TOOLTIP_SHOW:
        this.showTooltip(data.x, data.y, data.html)
        break
      case MSG.TOOLTIP_HIDE:
        this.hideTooltip()
        break
      case MSG.ERROR:
        this.$log_error?.('SpilkaChartsMF', data.message)
        break
      }
    },
    /**
     * Тултіп рендериться поза деревом компонента (прямий DOM-вузол у document.body), бо має
     * позиціюватись через position:fixed відносно вʼюпорта сторінки, а не iframe — інакше він
     * так само обрізався б межею iframe, як і рідний тултіп ApexCharts.
     */
    showTooltip(x, y, html) {
      const frameRect = this.$refs.chartFrame?.getBoundingClientRect()
      if (!frameRect) { return }
      if (!this.tooltipEl) {
        this.tooltipEl = document.createElement('div')
        this.tooltipEl.className = 'spilka-charts-tooltip'
        document.body.appendChild(this.tooltipEl)
      }
      this.tooltipEl.innerHTML = html
      this.tooltipEl.classList.toggle('spilka-charts-tooltip--dark', this.userTheme === DARK_THEME)
      this.tooltipEl.style.display = 'block'

      let left = frameRect.left + x + 12
      let top = frameRect.top + y - 12
      this.tooltipEl.style.left = `${left}px`
      this.tooltipEl.style.top = `${top}px`

      const rect = this.tooltipEl.getBoundingClientRect()
      if (rect.right > window.innerWidth) { left -= rect.right - window.innerWidth + 8 }
      if (rect.bottom > window.innerHeight) { top -= rect.bottom - window.innerHeight + 8 }
      if (rect.top < 0) { top = 4 }
      if (rect.left < 0) { left = 4 }
      this.tooltipEl.style.left = `${left}px`
      this.tooltipEl.style.top = `${top}px`
    },
    hideTooltip() {
      if (this.tooltipEl) { this.tooltipEl.style.display = 'none' }
    },
  },
  mounted() {
    window.addEventListener('message', this.onFrameMessage)

    if (typeof IntersectionObserver === 'undefined') {
      this.loaded = true
      return
    }
    const options = {
      root: document.querySelector('.main_container'),
      rootMargin: '400px',
      threshold: 0.01,
    }
    this.observer = new IntersectionObserver(elements => {
      elements.forEach(element => {
        if (element.isIntersecting && !this.loaded) {
          this.observer.unobserve(element.target)
          this.observer = null
        }
        this.loaded ||= element.isIntersecting
      })
    }, options)
    this.observer.observe(this.$refs.root)
  },
  beforeDestroy() {
    window.removeEventListener('message', this.onFrameMessage)
    if (this.observer) {
      this.observer.unobserve(this.$refs.root)
    }
    if (this.tooltipEl) {
      this.tooltipEl.remove()
      this.tooltipEl = null
    }
  },
  watch: {
    payload: {
      deep: true,
      handler() {
        this.pushToFrame()
      },
    },
    userTheme(newValue, oldValue) {
      if (newValue !== oldValue && this.iframeReady) {
        this.sendTheme()
      }
    },
  },
  props: {
    kind: {
      type: String,
      required: true,
    },
    height: [Number, String],
    width: [Number, String],
  },
}
</script>

<style scoped>
.charts-frame-wrapper {
  display: block;
  position: relative;
}
.charts-frame {
  border: 0;
  display: block;
}
.charts-skeleton {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: charts-skeleton-pulse 1.2s ease-in-out infinite;
}
.charts-skeleton--donut::before {
  content: '';
  display: block;
  height: 70%;
  aspect-ratio: 1 / 1;
  max-width: 100%;
  border-radius: 50%;
  border: 14px solid var(--color-disabled);
  box-sizing: border-box;
}
.charts-skeleton--rect::before {
  content: '';
  display: block;
  width: 90%;
  height: 80%;
  border-radius: 8px;
  background: var(--color-disabled);
}
@keyframes charts-skeleton-pulse {
  0%, 100% { opacity: 0.25; }
  50% { opacity: 0.55; }
}
</style>

<style>
/* Не scoped: цей вузол створюється напряму в document.body (поза деревом компонента),
   scoped-атрибут на нього все одно не потрапив би. */
.spilka-charts-tooltip {
  position: fixed;
  z-index: 2000;
  display: none;
  pointer-events: none;
  padding: 6px 10px;
  border-radius: 5px;
  font-size: 13px;
  line-height: 1.4;
  box-shadow: 2px 2px 6px -4px #999;
  border: 1px solid #e3e3e3;
  background: rgba(255, 255, 255, .96);
  color: #373d3f;
}
.spilka-charts-tooltip--dark {
  border: none;
  background: rgba(30, 30, 30, .9);
  color: #fff;
}
.spilka-charts-tooltip .chart-tooltip__series {
  font-weight: 600;
}
.spilka-charts-tooltip .chart-tooltip__value {
  font-weight: 600;
}
</style>
